/**
 * Workflow Service: SFTP / workflow-step connectivity test (Story 3.3b).
 * Execution stays server-side; UI only POSTs a step snapshot and displays sanitized outcomes.
 */

import {
	effectiveSftpAuthType,
	normalizeSftpAuthType,
	SFTP_AUTH_TYPE,
	type SftpAuthType,
} from "../constants/sftp-auth-type";
import { isJhaNetworkSftpStorageType } from "../constants/jha-network-storage-type";
import { normalizeStep } from "./api-normalizer";
import { Apis } from "./api-constants";
import { postRequest } from "./jha-fetch";
import { getErrorMessage } from "./getErrorMessage";

export type WorkflowStepTestLeg = "source" | "destination";

export interface WorkflowStepTestConnectionRequest {
	/** Saved workflow key when editing an existing workflow (optional for brand-new unsaved drafts). */
	sourceLocationPath?: string;
	workflowName?: string;
	stepIndex: number;
	testLeg: WorkflowStepTestLeg;
	step: Record<string, unknown>;
}

/** Infer API leg from operator "Location Type" label (free text). */
export function inferSftpTestLeg(locationType: string | undefined | null): WorkflowStepTestLeg {
	const s = String(locationType ?? "").toLowerCase();
	if (/\b(destination|dest)\b/.test(s)) return "destination";
	return "source";
}

/** Effective Password/SshKey for connectivity payload — respects per-leg auth when source ≠ destination (File Copy). */
export function effectiveSftpAuthTypeForTestLeg(
	formData: Record<string, unknown>,
	leg: WorkflowStepTestLeg
): SftpAuthType {
	const fallback = normalizeSftpAuthType((formData.AuthType ?? formData.authType) as string);
	const raw =
		leg === "destination"
			? (formData.destinationAuthType ?? formData.DestinationAuthType ?? fallback)
			: (formData.sourceAuthType ?? formData.SourceAuthType ?? fallback);
	return effectiveSftpAuthType(raw as string);
}

/**
 * WorkflowStepsView uses 1-based `stepIndex`; default template uses `0` as uninitialized — coerce non-positive and NaN to 1.
 */
export function resolveWorkflowStepIndexForTestConnection(stepIndex: unknown): number {
	if (stepIndex === undefined || stepIndex === null) return 1;
	const n = Number(stepIndex);
	if (!Number.isFinite(n) || n < 1) return 1;
	return n;
}

/** Pure guard logic for the SFTP Test connection button (unit-tested; popup passes live flags). */
export function canRunWorkflowStepSftpConnectivityTest(opts: {
	testConnectionUiEnabled: boolean;
	isSftpEndpoint: boolean;
	useTemplateVariables: boolean;
	isViewOnly: boolean;
	sshKeyAuthFeatureEnabled: boolean;
	isSftpSshKeyAuth: boolean;
	phase: "idle" | "loading" | "done";
	privateKeyBlockingMessage: string | null | undefined;
	/** Template mode: templateFormValid; regular workflows: page-2 / credential readiness (not templateFormValid). */
	formReadyForTest: boolean;
}): boolean {
	if (!opts.testConnectionUiEnabled) return false;
	if (!opts.isSftpEndpoint || opts.useTemplateVariables || opts.isViewOnly) return false;
	if (!opts.sshKeyAuthFeatureEnabled && opts.isSftpSshKeyAuth) return false;
	if (opts.phase === "loading") return false;
	if (opts.privateKeyBlockingMessage) return false;
	return !!opts.formReadyForTest;
}

/**
 * Remove PEM blocks and obvious secret material from strings shown to operators (NFR-S1).
 */
export function sanitizeConnectivityDisplayMessage(text: string): string {
	if (!text) return "";
	let t = text;
	t = t.replace(/-----BEGIN[\sA-Z0-9+-]+-----[\s\S]*?-----END[\sA-Z0-9+-]+-----/g, "[redacted]");
	t = t.replace(/\b(privateKey|sshPrivateKey|password|secretValue)\s*[:=]\s*\S+/gi, "[redacted]");
	return t.trim();
}

export type TestConnectionFailureCategory = "authentication" | "network" | "hostKey" | "generic";

function normalizeFailureCategory(raw: string | undefined | null): TestConnectionFailureCategory | undefined {
	if (!raw) return undefined;
	const s = String(raw).toLowerCase();
	if (s.includes("auth")) return "authentication";
	if (s.includes("network") || s.includes("reach") || s.includes("dns") || s.includes("timeout")) return "network";
	if (s.includes("host") && s.includes("key")) return "hostKey";
	return "generic";
}

export interface ParsedTestConnectionResult {
	ok: boolean;
	message: string;
	category?: TestConnectionFailureCategory;
}

/**
 * Map Workflow Service style JSON to operator-facing copy. Never forwards raw secret fields.
 */
export function parseWorkflowStepTestConnectionResponse(payload: unknown): ParsedTestConnectionResult {
	if (!payload || typeof payload !== "object") {
		return { ok: false, message: "Unexpected response from test connection.", category: "generic" };
	}
	const p = payload as Record<string, unknown>;
	if (p.status && typeof p.status === "number" && p.status >= 400) {
		return { ok: false, message: "Test connection is not available (HTTP error).", category: "generic" };
	}
	if (p.messageStatus === null || p.messageStatus === undefined) {
		const msg =
			(typeof p.message === "string" && p.message) ||
			(typeof p.summary === "string" && p.summary) ||
			"Connection test succeeded.";
		return { ok: true, message: sanitizeConnectivityDisplayMessage(msg) };
	}
	const details = p.details as unknown;
	const rawMsg =
		Array.isArray(details) && details.length > 0
			? sanitizeConnectivityDisplayMessage(getErrorMessage(details))
			: sanitizeConnectivityDisplayMessage(String(p.messageStatus ?? "Connection test failed."));
	const cat =
		normalizeFailureCategory(
			(typeof p.failureCategory === "string" && p.failureCategory) ||
				(typeof p.errorCategory === "string" && p.errorCategory) ||
				(typeof p.category === "string" && p.category) ||
				undefined
		) ?? inferCategoryFromMessage(rawMsg);
	const labeled =
		cat === "authentication"
			? `Authentication: ${rawMsg}`
			: cat === "network"
				? `Network or reachability: ${rawMsg}`
				: cat === "hostKey"
					? `Host key / TLS: ${rawMsg}`
					: rawMsg;
	return { ok: false, message: labeled, category: cat };
}

function inferCategoryFromMessage(msg: string): TestConnectionFailureCategory {
	const m = msg.toLowerCase();
	if (/auth|password|credential|denied|permission/.test(m)) return "authentication";
	if (/timeout|unreachable|network|dns|refused|connection/.test(m)) return "network";
	if (/host\s*key|certificate|tls|ssl/.test(m)) return "hostKey";
	return "generic";
}

function stripMask(v: string): string {
	return String(v ?? "").replace(/\*/g, "");
}

/**
 * Build secretValue for SFTP similarly to AddWorkflowStepPopup.addSecretValue so the test payload aligns with save shape.
 * Uses `testLeg` so password vs SSH key matches source vs destination when auth types differ (File Copy).
 */
export function buildSftpSecretValueMapForTest(
	formData: Record<string, unknown>,
	isEdit: boolean,
	testLeg: WorkflowStepTestLeg = "source"
): Record<string, string> | null {
	const auth = effectiveSftpAuthTypeForTestLeg(formData, testLeg);
	const hostOk =
		String(formData.HostName ?? "").trim() !== "" &&
		String(formData.Port ?? "").trim() !== "" &&
		String(formData.UserName ?? "").trim() !== "";
	const pwdOk = auth === SFTP_AUTH_TYPE.Password && String(formData.Password ?? "").trim() !== "";
	const sv = (formData.secretValue ?? {}) as Record<string, unknown>;
	const rawPk = stripMask(String(formData.privateKey ?? ""));
	const fromSv = stripMask(String(sv.privateKey ?? sv.PrivateKey ?? ""));
	const pkMaterial = rawPk || fromSv;
	const keyOk =
		auth === SFTP_AUTH_TYPE.SshKey &&
		(pkMaterial !== "" || (isEdit && !!(sv.privateKey ?? sv.PrivateKey)));
	if (!hostOk || !(pwdOk || keyOk)) return null;
	const Host = stripMask(String(formData.HostName ?? ""));
	const UserName = stripMask(String(formData.UserName ?? ""));
	const out: Record<string, string> = {
		Host,
		Port: stripMask(String(formData.Port ?? "")),
		UserName,
	};
	if (auth === SFTP_AUTH_TYPE.Password) {
		out.Password = stripMask(String(formData.Password ?? ""));
	} else if (pkMaterial) {
		out.privateKey = pkMaterial;
	} else if (isEdit && fromSv) {
		out.privateKey = fromSv;
	}
	return out;
}

/**
 * Produce normalized step payload for POST body (camelCase per api-normalizer).
 */
export function buildSftpStepSnapshotForConnectivityTest(
	formData: Record<string, unknown>,
	isEdit: boolean,
	testLeg: WorkflowStepTestLeg = "source"
): Record<string, unknown> {
	const clone: Record<string, unknown> = { ...formData };
	const sv = buildSftpSecretValueMapForTest(clone, isEdit, testLeg);
	if (sv) {
		clone.secretValue = sv;
	}
	const st = String(formData.storageType ?? "").trim();
	clone.storageType = isJhaNetworkSftpStorageType(st) ? st.toLowerCase() : "sftp";
	const normalized = normalizeStep(clone);
	return { ...(normalized as unknown as Record<string, unknown>) };
}

export async function postWorkflowStepTestConnection(
	body: WorkflowStepTestConnectionRequest
): Promise<unknown> {
	return postRequest(Apis.TEST_WORKFLOW_STEP_CONNECTION, body, undefined, true);
}
